import { EventBusy } from "@mui/icons-material";
import {Box, Grid2, Tooltip} from "@mui/material";
import isPast from "date-fns/isPast";
import isSameDay from "date-fns/isSameDay";
import React, {useMemo} from "react";

import { LocationHolidayOccurrence } from "../../../components/holidays/controller/use-location-holidays";
import { getOtherZoneAbbreviation } from "../../../components/holidays/utils/holiday-dates";
import {DayColumnHeader} from "./styled-components";
import { getShiftEffectiveStartDay } from "./utils";

const HOLIDAY_BACKGROUND = "#FDF0E6";

const MemoizedDayColumnHeader = (({ day, offsetStartTime = 0, holidayOccurrences, children, ...props }: { day: Date, offsetStartTime?: number, holidayOccurrences?: LocationHolidayOccurrence[] } & React.ComponentProps<typeof Grid2>) => {
    const isHoliday = (holidayOccurrences ?? []).length > 0;

    const styles = useMemo(
        () => {
          const effectiveToday = getShiftEffectiveStartDay(new Date(), offsetStartTime); // Adjust current date with offset
          // Today's highlight takes precedence over the holiday tint
          return {
            backgroundColor: isSameDay(day, effectiveToday)
              ? "#2F4D8B29"
              : isHoliday
                ? HOLIDAY_BACKGROUND
                : isPast(day)
                  ? "rgba(0,0,0,0.02)"
                  : "#fff",
            ...(isHoliday ? { position: "relative" as const, overflow: "hidden" } : {}),
          }
        },
        [day, offsetStartTime, isHoliday]
    );

    // "{name}", plus the Location for a Location's own holiday (Company
    // Holidays apply everywhere), plus "(EDT)" for the Location's time zone only when
    // the viewer is in a different time zone from the Location.
    const tooltipTitle = useMemo(
        () =>
          (holidayOccurrences ?? []).map((occurrence) => {
            const zone = getOtherZoneAbbreviation(occurrence.dateKey, occurrence.timeZone);
            const parts = [occurrence.holiday.name];
            if (occurrence.holiday.locationId !== null && occurrence.locationName) {
              parts.push(occurrence.locationName);
            }
            return (
              <div key={`${occurrence.locationId}-${occurrence.holiday.id}`}>
                {zone ? `${parts.join(" · ")} (${zone})` : parts.join(" · ")}
              </div>
            );
          }),
        [holidayOccurrences]
    );

    if (!isHoliday) {
      return <DayColumnHeader style={styles} {...props}>{children}</DayColumnHeader>;
    }

    return (
      <Tooltip title={tooltipTitle} placement="bottom">
        <DayColumnHeader style={styles} {...props}>
          <EventBusy
            aria-hidden
            sx={{
              position: "absolute",
              top: -10,
              right: -8,
              fontSize: 72,
              color: "#ED6C02",
              opacity: 0.12,
              transform: "rotate(-8deg)",
              pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "relative" }}>{children}</Box>
        </DayColumnHeader>
      </Tooltip>
    );
});

export default React.memo(MemoizedDayColumnHeader);
